<template>
  <vue-recaptcha
    v-if="showCaptchaRef && typeCaptchaRef === typeCaptcha.RE_CAPTCHA"
    ref="$vueRecaptchaRef"
    :sitekey="recaptchaSiteKeyRef"
    size="normal"
    theme="light"
    placeholder="Recaptcha"
    class="card-register__content__form__recaptcha"
    @verify="captchaVerified"
    @expire="captchaExpired"
    @fail="captchaFailed"
  />

  <vue-hcaptcha
    v-else-if="showCaptchaRef && typeCaptchaRef === typeCaptcha.H_CAPTCHA"
    ref="$vueHcaptchaRef"
    size="normal"
    theme="light"
    placeholder="Hcaptcha"
    class="card-register__content__form__recaptcha"
    :sitekey="hcaptchaSiteKeyRef"
    @verify="captchaVerified"
    @expire="captchaExpired"
    @error="captchaError"
  />

  <altcha-widget
    v-else-if="showCaptchaRef && typeCaptchaRef === typeCaptcha.ALT_CAPTCHA"
    ref="$altchaRef"
    :data-site="altchaSiteKeyRef"
    class="captcha__altcha"
    style="--altcha-max-width: 100%"
    test
    @statechange="captchaStateChange"
    @verified="captchaVerified"
  />
</template>

<script setup>
import {inject, onMounted, shallowRef} from 'vue';

// #region Enums
import {typeCaptcha} from '@/enum/config.enum.js';
import {StatusCodes as HttpStatus} from 'http-status-codes';
// #endregion

import {useDev} from '@sttedra_ui/framework';

// #region Composables
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import vueRecaptcha from 'vue3-recaptcha2';

// #region API
import {
  getTypeCaptcha as getTypeCaptchaApi,
  validateCaptcha as validateCaptchaApi,
} from '@/api/register/register.api';
// #endregion

const emits = defineEmits('update:modelValue');
const appState$$ = inject('appState$$');

const showCaptchaRef = shallowRef(false);
const typeCaptchaRef = shallowRef(typeCaptcha.RE_CAPTCHA);

const $vueRecaptchaRef = shallowRef(null);
const recaptchaSiteKeyRef = shallowRef(STTEDRA_SITE_KEY_CAPTCHA);
const $vueHcaptchaRef = shallowRef(null);
const hcaptchaSiteKeyRef = shallowRef(STTEDRA_SITE_KEY_H_CAPTCHA);

const $altchaRef = shallowRef(null);
const altchaSiteKeyRef = shallowRef(STTEDRA_SITE_KEY_ALT_CAPTCHA);

/**
 * Determines whether the CAPTCHA component should be visible.
 *
 * @returns {boolean} `true` if the CAPTCHA is currently visible; otherwise `false`.
 */
function isCaptchaVisible() {
  return showCaptchaRef.value;
}

/**
 * Handles a successful CAPTCHA verification event.
 *
 * Extracts the token from the event detail, event target value, or use the raw response.
 * Logs the verified token and emits it via the `update:modelValue` event.
 *
 * @param {CustomEvent|Event|string|null} response - The CAPTCHA response, which may be a CustomEvent,
 *                                                   a native Event with a target value, a raw string token,
 *                                                   or null/undefined.
 * @returns {void}
 * @throws {Error} If the `emits` function is not defined.
 */
function captchaVerified(response) {
  const token =
    response?.detail ??
    (response?.target && response.target.value) ??
    (typeof response === 'string' ? response : null);

  emits('update:modelValue', token);
}

/**
 * Handles the CAPTCHA expiration event.
 *
 * Emits `null` via `update:modelValue` to clear the CAPTCHA token.
 *
 * @returns {void}
 * @throws {Error} If the `emits` function is not defined.
 */
function captchaExpired() {
  emits('update:modelValue', null);
}

/**
 * Handles a CAPTCHA error event.
 *
 * Emits `null` via `update:modelValue` to clear the CAPTCHA token.
 *
 * @returns {void}
 * @throws {Error} If the `emits` function is not defined.
 */
function captchaError() {
  emits('update:modelValue', null);
}

/**
 * Maneja los cambios de estado del CAPTCHA.
 *
 * @param {Event} e - Objeto de evento con detalles del CAPTCHA.
 * @param {{state: 'initial'|'loading'|'ready'|'solved'|'error', id: string}} e.detail - Detalles del estado y el identificador del CAPTCHA.
 */
function captchaStateChange(e) {
  const {state, id} = e.detail ?? {};
  const newValue = state === 'solved' ? id : null;
  emits('update:modelValue', newValue);
}

/**
 * Resets the CAPTCHAs
 */
function resetCaptcha() {
  if (showCaptchaRef.value) {
    $vueRecaptchaRef?.value?.reset();
    $vueHcaptchaRef?.value?.reset();
    $altchaRef.value?.reset();
    emits('update:modelValue', null);
  }
}

/**
 * Determines whether to show CAPTCHA based on API validation.
 *
 * @async
 * @function isShowCaptcha
 * @returns {Promise<boolean>} - Returns `true` if CAPTCHA should be shown, otherwise `false`.
 * @throws Will log an error and return `false` if the API call fails.
 */
async function isShowCaptcha() {
  try {
    const {data, response} = await validateCaptchaApi();

    if (response?.status !== HttpStatus.OK) {
      return false;
    }

    return Boolean(data);
  } catch (error) {
    useDev.error('[CaptchaUtils.js] isShowCaptcha', error);
    return false;
  }
}

/**
 * Retrieves the type of CAPTCHA to display based on API response.
 *
 * @async
 * @function getTypeCaptcha
 * @returns {Promise<string>} - Returns the CAPTCHA type. Defaults to `typeCaptcha.RE_CAPTCHA` if the API call fails or data is incomplete.
 * @throws Will log an error and return the default CAPTCHA type if the API call fails.
 */
async function getTypeCaptcha() {
  try {
    const {data, response} = await getTypeCaptchaApi();

    if (response?.status !== HttpStatus.OK) {
      return typeCaptcha.RE_CAPTCHA;
    }

    return data?.type || typeCaptcha.RE_CAPTCHA;
  } catch (error) {
    useDev.error('[CaptchaUtils.js] getTypeCaptcha', error);
    return typeCaptcha.RE_CAPTCHA;
  }
}

onMounted(async () => {
  showCaptchaRef.value = (await isShowCaptcha()) && !appState$$.isLogged;
  typeCaptchaRef.value = await getTypeCaptcha();
});

defineExpose({
  resetCaptcha,
  isCaptchaVisible,
});
</script>

<style lang="scss">
.captcha {
  &__altcha {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    .altcha {
      width: 100%;
      height: 100%;
      margin-bottom: 2rem;
    }
  }
}
</style>
